import {designspec} from '../apis/designspec';
import t from '../../main/i18n';

const uid = () => `${Math.floor(Math.random() * 1e9)}`.padEnd(9, '0');

const MAX_PROJECT_SECTIONS = 10;
const state = {
  coverImages: [],
  projects: [],
  onboarded: false
};

const getters = {
  authenticated: (_state, _getters, _rootState, rootGetters) => {
    return rootGetters['auth/authenticated'];
  },
  project: state => id =>
    state.projects.find(project => `${project.id}` === `${id}`),
  projectName: (_, getters) => id => getters.project(id).name || null,
  selectIndex: (_, getters) => (id, productId) =>
    ((getters.project(id).meta || {}).checked || []).indexOf(productId),
  isChecked: (_, getters) => (id, productId) =>
    ((getters.project(id).meta || {}).checked || []).indexOf(productId) !==
    -1,
  sections: (_, getters) => id => getters.project(id).sections,
  section: (_, getters) => (projectId, sectionId) =>
    getters.sections(projectId).find(s => s.id === sectionId)
};

const actions = {
  async getProjects({state, commit, getters}) {
    const isAuth = getters.authenticated;
    if (isAuth) {
      const projects = await designspec.getProjects();
      commit('setProjects', projects);
      return projects;
    } else {
      if (state.projects.length > 0) {
        const localProjects = state.projects.filter(v => v.id === 0);
        commit('setProjects', localProjects);
        return localProjects;
      }
      return state.projects;
    }
  },
  async createProject({state, commit, getters, dispatch}, project) {
    const cover_image_id = project.coverImageId || null;
    delete project.coverImageId;
    const productIds = project.productIds || [];
    delete project.productIds;
    const variationIds = project.variationIds || [];
    delete project.variationIds;
    const isAuth = getters.authenticated;

    if (isAuth) {
      const response = await designspec.createProject({
        ...project,
        cover_image_id
      });
      const newProject = Object.assign({}, defaultProject, response);

      const projects = await designspec.getProjects();
      commit('setProjects', projects);

      if (productIds.length > 0 || variationIds.length > 0) {
        await dispatch('addSection', {
          project: newProject.id,
          name: t('New Section'),
          products: [
            ...productIds.map(id => ({
              id,
              type: 'product'
            })),
            ...variationIds.map(id => ({
              id,
              type: 'variation'
            }))
          ]
        });
        const projects = await designspec.getProjects();
        commit('setProjects', projects);
      }

      return newProject.id;
    } else {
      const id = 0;
      const date = `${new Date().valueOf()}`;
      const newProject = Object.assign(
        {id, date, cover_image_id},
        defaultProject,
        project
      );
      const projects = [...state.projects, newProject];
      commit('setProjects', projects);

      if (productIds.length > 0 || variationIds.length > 0) {
        await dispatch('addSection', {
          project: id,
          name: 'New Section',
          products: [
            ...productIds.map(id => ({
              id,
              type: 'product'
            })),
            ...variationIds.map(id => ({
              id,
              type: 'variation'
            }))
          ]
        });
      }
      return id;
    }
  },
  async deleteProject({state, commit, getters}, project) {
    const isAuth = getters.authenticated;
    if (isAuth) {
      await designspec.deleteProject(project);
      const projects = await designspec.getProjects();
      commit('setProjects', projects);
    } else {
      const projects = state.projects.slice(0);
      const index = projects.findIndex(p => p.id === project.id);
      projects.splice(index, 1);
      commit('setProjects', projects);
    }
  },
  async updateProject({commit, getters}, p) {
    const project = getters.project(p.id);
    const newProject = Object.assign({}, project, p);
    commit('setProject', newProject);
    return newProject;
  },
  async saveProject({getters}, {id}) {
    const isAuth = getters.authenticated;
    const project = getters.project(id);
    if (isAuth) {
      await designspec.updateProject(project);
    }
    return project;
  },
  async updateProjectMeta({commit, getters}, project) {
    if (!project?.id) return;
    const updatedMeta = {...project};
    delete updatedMeta.id;
    const existingProject = getters.project(project.id);
    let newProject = existingProject;
    if (existingProject?.meta) {
      newProject = {
        ...existingProject,
        meta: {...existingProject.meta, ...updatedMeta}
      };
    }
    commit('setProject', newProject);
    return newProject;
  },
  async updateSectionName(
    {dispatch, getters},
    {project: projectId, section: sectionId, name}
  ) {
    const project = getters.project(projectId);
    const index = project.sections.findIndex(s => s.id === sectionId);
    const sections = project.sections.slice(0);
    const section = sections[index];
    const newSections = sections.slice(0);
    newSections.splice(index, 1, Object.assign({}, section, {name}));

    dispatch('updateProject', {
      id: projectId,
      sections: newSections
    });
  },
  async addSection({getters, dispatch}, section) {
    const projectId = section.project;
    delete section.project;
    const project = getters.project(Number(projectId));
    const sections = project.sections || [];

    if (sections.length >= MAX_PROJECT_SECTIONS) return;

    if (typeof section === 'object' && !('id' in section)) {
      section.id = uid();
    }
    if (typeof section === 'object' && !('name' in section)) {
      section.name = 'New Section';
    }
    if (typeof section === 'object' && !('products' in section)) {
      section.products = [];
    }
    if (typeof section === 'object' && !('meta' in section)) {
      section.meta = {};
    }

    const newSections = sections.slice();
    newSections.splice(0, 0, section);

    dispatch('updateProject', {
      id: projectId,
      sections: newSections
    }).then(() => dispatch('saveProject', {id: projectId}));
  },
  toggleSection({getters, dispatch}, section) {
    const projectId = section.project;
    delete section.project;
    const project = getters.project(projectId);
    const sections = project.sections.map(s => {
      if (s.id === section.section) {
        return Object.assign({}, s, {
          meta: Object.assign({}, s.meta || {}, {
            collapsed: !(s.meta || {}).collapsed
          })
        });
      }
      return s;
    });

    dispatch('updateProject', {
      id: projectId,
      sections
    });
  },
  removeSection({getters, dispatch}, section) {
    const projectId = section.project;
    delete section.project;
    const project = getters.project(projectId);
    const index = project.sections.findIndex(
      s => s.id === section.section
    );
    if (index === -1) return;
    const sections = project.sections.slice(0);
    sections.splice(index, 1);

    dispatch('updateProject', {
      id: projectId,
      sections
    }).then(() => dispatch('saveProject', {id: projectId}));
  },
  async removeProduct(
    {getters, dispatch},
    {project: projectId, section: sectionId, product: productId}
  ) {
    const project = getters.project(projectId);
    const item = {section: sectionId, product: productId};
    const sections = project.sections.slice(0);

    const sectionIndex = sections.findIndex(s => s.id === item.section);
    const section = sections[sectionIndex];
    const index = section.products.findIndex(
      p => `${p.id}` === `${item.product}`
    );
    if (index === -1) return;
    const products = section.products.slice(0);
    products.splice(index, 1);
    sections.splice(
      sectionIndex,
      1,
      Object.assign({}, section, {products})
    );

    dispatch('updateProject', {
      id: projectId,
      sections
    }).then(() => dispatch('saveProject', {id: projectId}));
  },
  async setSections({dispatch}, {project: projectId, sections}) {
    dispatch('updateProject', {
      id: projectId,
      sections
    }).then(() => dispatch('saveProject', {id: projectId}));
  },
  async setProducts(
    {getters, dispatch},
    {project: projectId, section: sectionId, products}
  ) {
    const project = getters.project(projectId);
    if (!project?.sections) return;
    const sections = project.sections.map(s => {
      if (sectionId === s.id) {
        return Object.assign({}, s, {products});
      }
      return s;
    });

    dispatch('updateProject', {
      id: projectId,
      sections
    }).then(() => dispatch('saveProject', {id: projectId}));
  },
  async addProducts(
    {getters, dispatch},
    {project: projectId, sections: sectionIds, products}
  ) {
    const project = getters.project(projectId);
    if (
      !project.sections ||
      (project.sections && project.sections.length === 0)
    ) {
      dispatch('addSection', {
        project: projectId,
        name: 'New Section',
        products
      }).then(() => dispatch('saveProject', {id: projectId}));
    } else {
      const sections = project.sections.map(s => {
        if (sectionIds.includes(s.id) && s.products.length < 10) {
          return Object.assign({}, s, {
            products: [...s.products, ...products]
          });
        }
        return s;
      });

      dispatch('updateProject', {
        id: projectId,
        sections
      }).then(() => dispatch('saveProject', {id: projectId}));
    }
  },
  toggleProductChecked(
    {getters, dispatch},
    {project: projectId, section: sectionId, product: productId}
  ) {
    const id = `${sectionId}:${productId}`;
    const project = getters.project(projectId);
    const checkedProducts = project.meta.checked.slice(0);
    if (checkedProducts.indexOf(id) === -1) {
      checkedProducts.push(id);
    } else {
      checkedProducts.splice(checkedProducts.indexOf(id), 1);
    }

    const newProject = Object.assign({}, project, {
      meta: Object.assign({}, project.meta, {checked: checkedProducts})
    });

    dispatch('updateProject', newProject);
  }
};

const defaultProject = {
  name: '',
  meta: {
    isDragging: false,
    checked: []
  },
  sections: []
};

const mutations = {
  setOnboarded(state, onboarded) {
    state.onboarded = onboarded;
  },
  setProjects(state, projects) {
    state.projects = projects;
  },
  setCoverImages(state, coverImages) {
    state.coverImages = coverImages;
  },
  setProject(state, project) {
    const index = state.projects.findIndex(p => p.id === project.id);
    const projects = state.projects.slice(0);
    projects.splice(index, 1, project);
    state.projects = projects;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
